import { useFormContext } from 'react-hook-form';
import { Chip } from 'shared/mui/Chips';
import {
  DEFAULT_DATE_TIME_MASK,
  DEFAULT_SEPARATOR,
} from 'shared/mui/DatePicker/config/defaultValues';
import { useAnchor } from 'shared/mui/DatePicker/hooks/useAnchor';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { PopoverChip } from 'shared/mui/Filter/FilterChipsToolbar/FilterChips/components/PopoverChip/PopoverChip';
import { formatInputInTz } from 'utils/date';

export type FilterDateTimeRangeChipProps = {
  label: string;
  name: string;
  children: React.ReactNode;
  mask?: string;
  onDelete?: VoidFunction;
  timezoneName?: string;
};

export const FilterDateTimeRangeChip: React.FC<FilterDateTimeRangeChipProps> = ({
  children,
  label,
  name,
  onDelete,
  mask = DEFAULT_DATE_TIME_MASK,
  timezoneName,
}) => {
  const { handleClose, handleClick, anchorEl } = useAnchor<HTMLDivElement>();

  const { appliedFilters, setAppliedFilter } = useFilterContext();
  const { getValues, setValue } = useFormContext();

  if (!appliedFilters[name] || (!appliedFilters[name]?.[0] && !appliedFilters[name]?.[1])) {
    return <></>;
  }

  function onApply() {
    handleClose();
    setAppliedFilter(name, getValues(name));

    if (timezoneName) {
      setAppliedFilter(timezoneName, getValues(timezoneName));
    }
  }

  function onReset() {
    setAppliedFilter(name, appliedFilters?.[name]);
    setValue(name, appliedFilters?.[name]);

    if (timezoneName) {
      setValue(timezoneName, appliedFilters?.[timezoneName]);
    }
  }

  const open = Boolean(anchorEl);
  const [start, end] = appliedFilters?.[name] ?? [];
  const timezone = timezoneName ? appliedFilters?.[timezoneName] : undefined;
  const labelText = `${formatInputInTz(start, { format: mask, timezone })}${DEFAULT_SEPARATOR}${formatInputInTz(end, { format: mask, timezone })}`;

  return (
    <>
      <PopoverChip
        anchorEl={anchorEl}
        onApply={onApply}
        onClose={handleClose}
        onReset={onReset}
        open={open}
      >
        {children}
      </PopoverChip>
      <Chip
        label={`${label}: ${labelText}`}
        onClick={handleClick}
        onDelete={() => {
          onDelete && onDelete();
          setValue(name, [null, null]);
          setAppliedFilter(name, [null, null]);
        }}
        size="medium"
      />
    </>
  );
};
